import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Check, Info, Loader2, Pencil, Scissors } from 'lucide-react';
import { api } from '../../services/api';
import Dialog from '../ui/Dialog';
import DateTimeInput from '../ui/DateTimeInput';
import Field, { FormServerError } from '../ui/Field';
import { formatTime } from '../../utils/format';
import { toLocalInputValue } from './durationModel';
import {
  editableOverlaps, overlapConflictOf, overlapKindKey, overlapRange, shortenPreview,
  type OverlapConflict, type OverlapItem,
} from './overlapModel';

/**
 * D-291 — the ONE overlap notice of every place that saves a work log
 * (manual report, a task's "add log", "היום שלי", the documentation
 * suggestions, editing a log). When the server refused the save with the
 * structured WORK_LOG_OVERLAP it says WHAT the time overlaps ("חופף לרישום על
 * משימה «X», 10:00–11:30") and offers:
 *   - "לקצר אוטומטית עד ‹שעה›" — `onShorten` sends the save again, shorter;
 *   - "לפתוח את הרישום השני לעריכה" — a side panel over the current screen
 *     (no navigation; the form / timer underneath stays as it is). After its
 *     save, `onRetry` sends the original save again.
 * Any other error is shown exactly as before (FormServerError).
 */
export default function WorkLogOverlapNotice({ error, fallback, onShorten, shortenInWholeMinutes = false, onConfirmParallel, onRetry, busy = false, className = '', renderOther }: {
  error: unknown;
  fallback: string;
  /** How this form showed any OTHER error until now (default: FormServerError) — unchanged. */
  renderOther?: (error: unknown) => ReactNode;
  /** Absent → no "shorten" button (the form cannot send a shorter time). */
  onShorten?: (conflict: OverlapConflict) => void;
  /** D-299: the screen saves the shortened LENGTH in whole minutes (documentation suggestions) - the preview shows that exact end. */
  shortenInWholeMinutes?: boolean;
  /**
   * D-299 (QX-1302) - "כן, עבדתי במקביל": the form sends the SAME save again with
   * `confirm_parallel`. Absent (or the server did not offer it) → the notice is
   * the D-291 refusal exactly as before.
   */
  onConfirmParallel?: (conflict: OverlapConflict) => void;
  onRetry: () => void;
  busy?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const conflict = overlapConflictOf(error);
  const [editing, setEditing] = useState<OverlapItem | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  // DQ-044 - a double click on "כן, עבדתי במקביל" / "לקצר" sent the save twice before the
  // button was disabled (`busy` arrives one render later): two identical logs were created.
  const lastActRef = useRef(0);
  const once = (fn: () => void) => () => {
    const now = Date.now();
    if (busy || now - lastActRef.current < 1500) return;
    lastActRef.current = now;
    fn();
  };
  if (!error) return null;
  if (!conflict) return renderOther ? <>{renderOther(error)}</> : <FormServerError error={error} fallback={fallback} className={className} />;

  const editable = editableOverlaps(conflict);
  const fmtDay = (d: Date) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  const line = (o: OverlapItem) => {
    const params = {
      kind: t(`workLogOverlap.kind.${overlapKindKey(o.task_type)}`),
      // D-291c: a bidi isolate, so an English title inside «…» does not reorder the Hebrew sentence.
      title: `⁨${o.ticket_title || t('workLogOverlap.untitled')}⁩`,
      // An LTR island, so "10:00–11:30" never reads backwards in Hebrew.
      range: `⁦${overlapRange(o, (d) => formatTime(d), fmtDay, conflict.requestedStart)}⁩`,
    };
    return o.kind === 'timer' && !o.end_time ? t('workLogOverlap.lineTimer', params) : t('workLogOverlap.lineLog', params);
  };
  const shortenAt = conflict.suggestedEnd ? formatTime(conflict.suggestedEnd) : null;
  const askParallel = !!onConfirmParallel && conflict.canConfirmParallel === true;
  // D-299 (QX-1302): the exact result of shortening - the same numbers the server computed.
  const pv = shortenPreview(conflict, { wholeMinutes: shortenInWholeMinutes });
  const previewText = pv
    ? t('workLogOverlap.shortenPreview', {
        to: `⁦${formatTime(pv.to.start)}–${formatTime(pv.to.end)}⁩`,
        from: `⁦${formatTime(pv.from.start)}–${formatTime(pv.from.end)}⁩`,
      })
    : '';

  return (
    <div role="alert" data-overlap-notice="" className={`rounded-lg border border-danger-200 bg-danger-50 px-3 py-2.5 text-sm text-danger-700 ${className}`}>
      <p className="flex items-start gap-2 font-medium">
        <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
        <span className="min-w-0">{t(askParallel ? 'workLogOverlap.askParallel' : 'workLogOverlap.title')}</span>
      </p>
      <ul className="mt-1 space-y-1 ps-6">
        {conflict.overlaps.map((o) => (
          <li key={`${o.kind}-${o.id}`} className="flex flex-wrap items-center gap-x-2" data-overlap-line="">
            <bdi>{line(o)}</bdi>
            {editable.length > 1 && o.kind === 'log' && (
              <button type="button" className="text-xs underline hover:no-underline" disabled={busy} onClick={() => setEditing(o)}>
                {t('workLogOverlap.editThis')}
              </button>
            )}
          </li>
        ))}
      </ul>
      {!shortenAt && onShorten && <p className="mt-1 ps-6 text-xs text-text-2">{t('workLogOverlap.cannotShorten')}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-2 ps-6">
        {askParallel && (
          <button type="button" className="btn btn-primary btn-sm" disabled={busy} data-overlap-parallel="" onClick={once(() => onConfirmParallel!(conflict))}>
            {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Check size={14} aria-hidden="true" />}
            {t('workLogOverlap.confirmParallel')}
          </button>
        )}
        {editable.length > 0 && (
          <button type="button" className="btn btn-secondary btn-sm" disabled={busy} data-overlap-edit="" onClick={() => setEditing(editable[0])}>
            <Pencil size={14} aria-hidden="true" />
            {t(askParallel ? 'workLogOverlap.fixIt' : 'workLogOverlap.editOther')}
          </button>
        )}
        {onShorten && shortenAt && (
          <span className="inline-flex items-center gap-1">
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy} data-overlap-shorten="" title={askParallel ? previewText : undefined} onClick={once(() => onShorten(conflict))}>
              {busy && !askParallel ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Scissors size={14} aria-hidden="true" />}
              {askParallel ? t('workLogOverlap.shortenAuto') : t('workLogOverlap.shorten', { time: shortenAt })}
            </button>
            {askParallel && previewText && (
              // On a phone there is no hover: the info icon opens the exact preview.
              <button type="button" className="rounded-full p-1 text-danger-700 hover:bg-danger-100 sm:hidden" data-overlap-info="" aria-expanded={previewOpen}
                aria-label={t('workLogOverlap.shortenPreviewInfo')} onClick={() => setPreviewOpen((v) => !v)}>
                <Info size={14} aria-hidden="true" />
              </button>
            )}
          </span>
        )}
      </div>
      {askParallel && previewText && (
        <p className={`mt-1 ps-6 text-xs text-text-2 ${previewOpen ? '' : 'hidden'}`} data-overlap-preview="" role="note"><bdi>{previewText}</bdi></p>
      )}
      {editing && (
        <OverlapEditPanel
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onRetry(); }}
        />
      )}
    </div>
  );
}

/**
 * The other report, in a side panel over the current screen: its start and
 * end only (what clears the overlap). Only CHANGED times are sent, like the
 * work-logs edit window.
 */
function OverlapEditPanel({ item, onClose, onSaved }: { item: OverlapItem; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['work-log', item.id],
    queryFn: () => api.getWorkLog(item.id),
  });
  const loaded = data?.success ? data.data : null;
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [initial, setInitial] = useState({ start: '', end: '' });
  useEffect(() => {
    const s = loaded?.start_time ?? item.start_time;
    const e = loaded?.end_time ?? item.end_time;
    const st = s ? toLocalInputValue(new Date(s)) : '';
    const en = e ? toLocalInputValue(new Date(e)) : '';
    setStart(st); setEnd(en); setInitial({ start: st, end: en });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per loaded entry: later refetches must not overwrite edits
  }, [loaded?.id, item.id]);

  const mutation = useMutation({
    mutationFn: () => {
      const payload: { start_time?: string; end_time?: string } = {};
      if (start && start !== initial.start) payload.start_time = new Date(start).toISOString();
      if (end && end !== initial.end) payload.end_time = new Date(end).toISOString();
      return api.updateWorkLog(item.id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-logs'] });
      queryClient.invalidateQueries({ queryKey: ['work-log', item.id] });
      queryClient.invalidateQueries({ queryKey: ['work-records'] });
      onSaved();
    },
  });
  const changed = (start && start !== initial.start) || (end && end !== initial.end);
  const invalid = !!start && !!end && new Date(end).getTime() <= new Date(start).getTime();
  const formId = `overlap-edit-${item.id}`;

  return (
    <Dialog
      open
      onClose={onClose}
      variant="drawer"
      size="md"
      title={t('workLogOverlap.editTitle')}
      description={item.ticket_title || undefined}
      dismissible={!mutation.isPending}
      panelProps={{ 'data-overlap-edit-panel': '' }}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary">{t('common.cancel')}</button>
          <button type="submit" form={formId} disabled={mutation.isPending || !changed || invalid} className="btn btn-primary">
            {mutation.isPending && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            {t('workLogOverlap.saveAndRetry')}
          </button>
        </>
      )}
    >
      {/* The panel is portaled, but React bubbles its submit to the form underneath: stopped here. */}
      <form id={formId} noValidate className="space-y-4"
        onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); if (changed && !invalid) mutation.mutate(); }}>
        <p className="text-sm text-text-2">{t('workLogOverlap.editHint')}</p>
        {isLoading && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field id={`${formId}-start`} label={t('timer.start')}>
            <DateTimeInput dir="ltr" className="input text-start" value={start} onChange={(e) => setStart(e.target.value)} />
          </Field>
          <Field id={`${formId}-end`} label={t('timer.end')} error={invalid ? t('timer.endBeforeStart') : undefined}>
            <DateTimeInput dir="ltr" className="input text-start" value={end} max={toLocalInputValue(new Date())} onChange={(e) => setEnd(e.target.value)} />
          </Field>
        </div>
        <FormServerError error={mutation.error} fallback={t('workLogOverlap.editFailed')} />
      </form>
    </Dialog>
  );
}
